import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, Grid, IconButton } from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import AppLayout from '../../SideBar';
import HeadingComponent from '../../../components/ui/HeadingComponent/index.js';
import strings from '../../../constant/strings';
import { LoadingContainer } from '../../../assets/styles/CommonStyles';
import { getMyPreordersApi } from '../../../services/preorderService';
import { HeaderRow, PageContainer } from '../Shop/styles';
import {
  EmptyState, Muted, OrderCard, OrderCardBody, OrderCardSide, OrderGrid,
  OutlineButton, PrimaryButton, StatusPill,
} from '../../Preorder/styles';
import { formatPaise, formatWindow, isPaymentDue, statusLabel, takeJustPlaced } from '../../Preorder/utils';

// Statuses coloured the way a patient reads them: fine, needs attention, over.
const toneFor = (status) => {
  if (['confirmed', 'ready_to_ship', 'shipped', 'delivered', 'reserved'].includes(status)) return 'good';
  if (['awaiting_balance', 'balance_overdue', 'pending_payment', 'payment_failed'].includes(status)) return 'warn';
  if (status.startsWith('cancelled') || ['expired', 'refunded', 'refund_pending'].includes(status)) return 'bad';
  return 'neutral';
};

const MyPreordersContent = () => {
  const history = useHistory();
  const token = useSelector((state) => state.auth.token);
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('loading');
  // Set by the signup flow when an order was actually placed. Read once, so a
  // reload does not congratulate them again.
  const [justPlaced, setJustPlaced] = useState(() => takeJustPlaced());

  useEffect(() => {
    let cancelled = false;
    getMyPreordersApi({ token }).then((res) => {
      if (cancelled) return;
      if (res.response_type === 'fail') { setStatus('error'); return; }
      const list = res.response?.data || [];
      setOrders(list);
      setStatus(list.length ? 'ready' : 'empty');
    });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <PageContainer>
      {/* HeadingComponent renders two siblings, so it needs its own wrapper -
          dropped straight into this flex row the subtitle drifts to the middle. */}
      <HeaderRow>
        <div>
          <HeadingComponent text={{ primary: strings.myPreorders, secondary: strings.myPreordersHeadingText }} />
        </div>
        <OutlineButton onClick={() => history.push('/patient/shop')}>{strings.shop}</OutlineButton>
      </HeaderRow>

      {status === 'loading' ? <LoadingContainer>{strings.loading}</LoadingContainer> : null}

      {status === 'empty' ? (
        <EmptyState>
          <ShoppingBagOutlinedIcon sx={{ fontSize: 44, color: '#CFCFD7' }} />
          <h3>{strings.noPreorders}</h3>
          <Muted>{strings.noPreordersNote}</Muted>
        </EmptyState>
      ) : null}

      {status === 'error' ? (
        <EmptyState>
          <h3>{strings.preorderGenericError}</h3>
        </EmptyState>
      ) : null}

      {/* Shown here rather than on the page being left behind, so it survives
          the navigation that follows confirming the code. */}
      <Dialog
        open={Boolean(justPlaced)}
        onClose={() => setJustPlaced(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, textAlign: 'center', p: 1, position: 'relative' } }}
      >
        {/* A visible way out, not only the backdrop or Esc - this appears
            unannounced on arrival, so dismissing it should be obvious. */}
        <IconButton
          aria-label={strings.close}
          onClick={() => setJustPlaced(null)}
          sx={{
            position: 'absolute', top: 8, right: 8, color: '#9A9AAB',
            '&:hover': { color: '#2B2B2B', background: 'rgba(0,0,0,.04)' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <DialogContent sx={{ pt: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#2AAB6C' }} />
          <div style={{
            fontFamily: 'Poppins, sans-serif', fontSize: 19, fontWeight: 600,
            color: '#2B2B2B', marginTop: 12,
          }}
          >
            {strings.preorderPlacedTitle}
          </div>
          <Muted style={{ marginTop: 8 }}>{strings.preorderPlacedNote}</Muted>
          {justPlaced?.orderNumber ? (
            <Muted style={{ marginTop: 10, fontWeight: 600, color: '#4A4A5A' }}>
              {strings.orderNumber}: {justPlaced.orderNumber}
            </Muted>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <PrimaryButton onClick={() => setJustPlaced(null)}>{strings.viewMyPreorders}</PrimaryButton>
        </DialogActions>
      </Dialog>

      {status === 'ready' ? (
        <OrderGrid style={{ marginTop: 20 }}>
          {orders.map((order) => {
            const due = isPaymentDue(order);
            return (
              <OrderCard key={order.id} elevation={0} onClick={() => history.push(`/patient/preorders/${order.id}`)}>
                <OrderCardBody>
                  <div className="title-row">
                    <span className="title">{order.productTitle}</span>
                    <StatusPill data-tone={toneFor(order.status)}>{statusLabel(order.status)}</StatusPill>
                  </div>

                  <dl className="facts">
                    <div>
                      <dt>{strings.orderNumber}</dt>
                      <dd>{order.orderNumber}</dd>
                    </div>
                    <div>
                      <dt>{strings.quantity}</dt>
                      <dd>{order.qty}</dd>
                    </div>
                    <div>
                      <dt>{strings.expectedDispatch}</dt>
                      <dd>{formatWindow(order.expectedShipFrom, order.expectedShipTo)}</dd>
                    </div>
                  </dl>

                  {order.dueAmount > 0 ? (
                    <div className={due ? 'note note-due' : 'note'}>
                      {due
                        ? `${strings.paymentDueNow}: ${formatPaise(order.dueAmount)}`
                        : `${strings.dueLater}: ${formatPaise(order.dueAmount)} · ${strings.paymentCollectedBefore}`}
                    </div>
                  ) : null}
                </OrderCardBody>

                <OrderCardSide>
                  <div className="amount">{formatPaise(order.amounts?.total)}</div>
                  <OutlineButton onClick={(e) => {
                    e.stopPropagation();
                    history.push(`/patient/preorders/${order.id}`);
                  }}
                  >
                    {strings.viewPreorder}
                  </OutlineButton>
                </OrderCardSide>
              </OrderCard>
            );
          })}
        </OrderGrid>
      ) : null}
    </PageContainer>
  );
};

const MyPreorders = () => (
  <Grid container spacing={0}>
    <AppLayout>
      <MyPreordersContent />
    </AppLayout>
  </Grid>
);

export default MyPreorders;
